import { useParams, Link } from "react-router-dom";
import { ArrowUpRight, ArrowLeft, CheckCircle2, BookOpen, Phone } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { Reveal } from "@/components/common/Reveal";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { business, brands } from "@/data/site";
import NotFound from "@/pages/NotFound";

export default function BrandDetail() {
  const { slug } = useParams();
  const brand = brands.find((b) => b.slug === slug);
  if (!brand) return <NotFound />;

  return (
    <div>
      <PageHeader eyebrow={brand.tagline} title={brand.name} description={brand.blurb}>
        <div className="flex flex-wrap gap-3">
          <Button asChild variant="brand" size="lg">
            <a href={brand.url} target="_blank" rel="noopener noreferrer">
              Visit official site <ArrowUpRight className="h-4 w-4" />
            </a>
          </Button>
          <Button asChild variant="glass" size="lg">
            <Link to="/brands"><ArrowLeft className="h-4 w-4" /> All brands</Link>
          </Button>
        </div>
      </PageHeader>

      <section className="section-py">
        <div className="mx-auto grid max-w-7xl gap-8 container-px lg:grid-cols-3">
          <Reveal className="lg:col-span-2">
            <Card className="h-full border-border bg-card p-8">
              <h2 className="text-2xl font-bold text-foreground">Product categories</h2>
              <p className="mt-2 text-[15px] text-muted-foreground">
                Popular {brand.name} product lines we deliver, set up and repair.
              </p>
              <div className="mt-6 grid gap-4 sm:grid-cols-2">
                {brand.categories.map((c) => (
                  <div key={c} className="flex items-start gap-3 rounded-xl border border-border bg-muted/40 p-4">
                    <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-accent" />
                    <span className="text-[15px] font-medium text-foreground">{c}</span>
                  </div>
                ))}
              </div>
            </Card>
          </Reveal>

          <Reveal delay={0.1}>
            <Card className="flex h-full flex-col border-border bg-card p-8">
              <h2 className="flex items-center gap-2 text-2xl font-bold text-foreground">
                <BookOpen className="h-6 w-6 text-primary" /> Resources
              </h2>
              <p className="mt-2 text-[15px] text-muted-foreground">
                Manuals, support &amp; official links.
              </p>
              <ul className="mt-6 space-y-3">
                {brand.resources.map((r) => (
                  <li key={r.label}>
                    <a
                      href={r.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group flex items-center justify-between gap-3 rounded-xl border border-border bg-muted/40 px-4 py-3.5 text-[15px] font-medium text-foreground transition-colors hover:border-primary hover:bg-primary/5"
                    >
                      {r.label}
                      <ArrowUpRight className="h-4 w-4 text-muted-foreground transition-colors group-hover:text-primary" />
                    </a>
                  </li>
                ))}
              </ul>
              <div className="mt-auto pt-6">
                <p className="text-sm text-muted-foreground">Need {brand.name} service or parts?</p>
                <Button asChild variant="outlineDark" className="mt-3 w-full" size="lg">
                  <a href={business.phoneHref}><Phone className="h-4 w-4" /> {business.phone}</a>
                </Button>
              </div>
            </Card>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
